import { isDefined, isNotDefined } from '@/utils/assertions';
import { formatOnlyDate } from '@/utils/date';
import { format } from 'date-fns';
import { FormattedNumber, useIntl } from 'react-intl';
import type { FieldSpec } from './template';

interface FormattedValueProps {
  fieldSpec: FieldSpec;
  position: any;
}

const invalidFieldValue = <span>!ERROR!</span>;

export function FormattedValue({ fieldSpec, position }: FormattedValueProps) {
  const {locale} = useIntl()
  const amountWithCurrencyFormatter = new Intl.NumberFormat("fr-FR", { style: 'currency', currency: position.currency, currencyDisplay: 'code' })
  const percentageFormatter = new Intl.NumberFormat(locale, {
    style: 'percent',
    minimumFractionDigits: 2,
  });

  if (typeof fieldSpec === 'string') {
    const value = position[fieldSpec];
    return isDefined(value) ? String(value) : '-';
  }

  const value = position[fieldSpec.fieldName];
  if (isNotDefined(value)) {
    return '-';
  }

  switch (fieldSpec.kind) {
    case 'amount': {
      const numberValue = extractFloat(value);
      if (isNotDefined(position.currency) || numberValue === null) {
        return invalidFieldValue;
      }
      return (
        <p>{amountWithCurrencyFormatter.format(numberValue)}</p>
      );
    }
    case 'percent': {
      const numberValue = extractFloat(value);
      if (numberValue === null) {
        return invalidFieldValue;
      }
      return `${percentageFormatter.format(numberValue / 100)}`;
    }
    case 'date': {
      if (typeof value === 'boolean' || typeof value === 'object') {
        return invalidFieldValue;
      }
      if (
        fieldSpec.fieldName === 'managersCreationDateTime' ||
        fieldSpec.fieldName === 'managersLastUpdateDateTime'
      ) {
        const Hour = format(new Date(value), ' - h:mm a');
        return (
          <>
            {formatOnlyDate(new Date(value))}
            {Hour}
          </>
        );
      }
      return formatOnlyDate(new Date(value));
    }
    case 'number': {
      const numberValue = extractFloat(value);
      if (isNotDefined(position.currency) || numberValue === null) {
        return invalidFieldValue;
      }
      return <FormattedNumber minimumFractionDigits={0} maximumFractionDigits={20} value={numberValue} />;
    }
  }
}

function extractFloat(value: string | string[] | number | boolean): number | null {
  let numberValue: number;
  switch (typeof value) {
    case 'boolean':
      return null;
    case 'number':
      numberValue = value;
      break;
    case 'string': {
      numberValue = Number.parseFloat(value);
      if (Number.isNaN(numberValue)) {
        return null;
      }
      break;
    } // typeof value = 'object'
    default: return null
  }
  return numberValue;
}
